import { apiFetch } from "../../api/apiFetch";

/**
 * Notes about your API (from PostsController):
 * - GET endpoints use querystring params (no JSON body).
 * - POST endpoints use JSON body (Content-Type: application/json).
 * - Some endpoints require userid in query/body (legacy); getpost derives userid from JWT.
 *
 * IMPORTANT:
 * - Do not prefix URLs with "/api" here if your apiFetch base is already "/api/v1".
 *   Use "posts/..." not "/api/posts/..."
 */

async function readJsonOrNull(res) {
    if (res.status === 204) return null;
    const text = await res.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
}

function handleNon200(res) {
    if (res.status === 401 || res.status === 400) return res.status;
    return "error";
}

/** -----------------------------
 *  GET endpoints (querystring)
 *  ----------------------------- */

export async function getPosts(userid, index, count, type,  filter, appid) {
    const qs = new URLSearchParams({
        userid,
        index: index,
        count: count,
        type: type,
        filter: filter,
        appid: appid,
    });

    const response = await apiFetch(`posts/getposts?${qs.toString()}`, {
        method: "GET",
    });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

export async function getPostsByUser(userid, postuserid, index, count, type, appid) {
    const qs = new URLSearchParams({
        userid,
        postuserid,
        index: index,
        count: count,
        type: type,
        appid,
    });

    const response = await apiFetch(`posts/getpostsbyuser?${qs.toString()}`, {
        method: "GET",
    });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

export async function getNewPosts(userid, type, newestpostid, appid, filter = 0) {
    const qs = new URLSearchParams({
        userid,
        type,
        newestpostid,
        filter,
        appid,
    });

    const response = await apiFetch(`posts/getnewposts?${qs.toString()}`, {
        method: "GET",
    });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

export async function getUserPostedPhotoIDs(userid) {
    const qs = new URLSearchParams({ userid });

    const response = await apiFetch(`posts/getuserpostedphotoids?${qs.toString()}`, {
        method: "GET",
    });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

export async function getSavedPostIDs(userid, appid) {
    const qs = new URLSearchParams({ userid, appid });

    const response = await apiFetch(`posts/getsavedpostids?${qs.toString()}`, {
        method: "GET",
    });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

export async function getPost(postid) {
    // API: [HttpGet("getpost")] string postid
    const qs = new URLSearchParams({ postid });

    const response = await apiFetch(`posts/getpost?${qs.toString()}`, {
        method: "GET",
    });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

export async function getPostContent(postcontentid, userid) {
    // API: [HttpGet("getpostcontent")] string postcontentid, string userid
    const qs = new URLSearchParams({ postcontentid, userid });

    const response = await apiFetch(`posts/getpostcontent?${qs.toString()}`, {
        method: "GET",
    });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

export async function getPostTextCategories() {
    // NOTE: This action is not in the controller you pasted.
    // Keeping it as GET, but if this 404s you either need to add it back to API
    // or remove it from SPA.
    const response = await apiFetch(`posts/getposttextcategories`, { method: "GET" });

    if (response.status !== 200) return handleNon200(response);
    return await response.json();
}

/** -----------------------------
 *  POST endpoints (JSON body)
 *  ----------------------------- */

export async function getPostsByIdRange(userid, postids, index, count) {
    // API: [HttpPost("getpostsbyidrange")] PostGetRequests { UserID, IDs, Index, Count }
    const req = { UserID: userid, IDs: postids, Index: index, Count: count };

    const response = await apiFetch(`posts/getpostsbyidrange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status !== 200) {
        if (response.status === 401) return response.status;
        return "error";
    }

    return await response.json();
}

export async function newMobilePhotoPost(userid, textcontent, photocontent, appid) {
    // API: [HttpPost("createmobilephotopost")] PostRequest { UserID, PhotoContent, TextContent, AppID }
    const b64 = await toBase64(photocontent);
    const photob64 = b64.split(",")[1];

    const req = { UserID: userid, PhotoContent: photob64, TextContent: textcontent, AppID: appid };

    const response = await apiFetch(`posts/createmobilephotopost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newPost(userid, type, appid, category) {
    // API: [HttpPost("createpost")] PostRequest { UserID, OwnerID, OwnerType, Type, AppID, TextCategory }
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        Type: type,
        AppID: appid,
        TextCategory: category,
    };

    const response = await apiFetch(`posts/createpost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function repost(userid, postid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        PostID: postid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/repost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newBlogPost(userid, blogid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        RefID: blogid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/createblogpost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newEventPost(userid, eventid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        RefID: eventid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/createeventpost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newMarketPost(userid, itemid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        RefID: itemid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/createmarketpost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newSongPost(userid, songid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        RefID: songid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/createsongpost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newVideoPost(userid, videoid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        RefID: videoid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/createvideopost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newPhotoPost(userid, photoid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        RefID: photoid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/createphotopost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function newPdfDocumentPost(userid, docid, textcontent, appid) {
    const req = {
        UserID: userid,
        OwnerType: 0,
        OwnerID: userid,
        RefID: docid,
        TextContent: textcontent,
        AppID: appid,
    };

    const response = await apiFetch(`posts/createpdfdocumentpost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function savePost(userid, postid) {
    // API: [HttpPost("savepost")] PostRequest (validated via ModelState)
    const req = { UserID: userid, PostID: postid };

    const response = await apiFetch(`posts/savepost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function unsavePost(userid, postid) {
    const req = { UserID: userid, PostID: postid };

    const response = await apiFetch(`posts/unsavepost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function addTextContent(userid, postid, idx, type, content, category = "", coverB64 = "") {
    // API: [HttpPost("addpostcontent")] PostContentRequest
    const req = {
        UserID: userid,
        PostID: postid,
        Index: idx,
        Type: type,
        TextContent: content,
        Category: category,
        CoverB64: coverB64,
        // BinaryContent omitted for text content
    };

    const response = await apiFetch(`posts/addpostcontent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await readJsonOrNull(response);
    return null;
}

/**
 * Photo/video content: your controller does NOT have addpostphotocontent/addpostvideocontent endpoints.
 * Your supported general endpoint is:
 *   POST posts/addpostcontent  (BinaryContent byte[] in JSON)  OR
 *   POST posts/addmobilepostvideocontent (B64Content in JSON -> server converts)
 *
 * For large files, JSON byte[] is not great. For now, keep the mobile base64 route for video.
 * For photo, you currently only have mobile photo post creation, not "add photo content to existing post".
 *
 * If you truly need upload-as-formdata endpoints for post content, you should add them to the API.
 */

export async function addMobilePostVideoContent(userid, postid, idx, b64Content) {
    // API: [HttpPost("addmobilepostvideocontent")] PostContentRequest { UserID, PostID, Index, B64Content }
    const req = {
        UserID: userid,
        PostID: postid,
        Index: idx,
        B64Content: b64Content,
    };

    const response = await apiFetch(`posts/addmobilepostvideocontent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await readJsonOrNull(response);
    return null;
}

export async function editPostTextContent(contentId, textContent) {
    // API: [HttpPost("editposttextcontent")] PostContentRequest { ID, TextContent }
    const req = { ID: contentId, TextContent: textContent };

    const response = await apiFetch(`posts/editposttextcontent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function deletePost(postid) {
    // API: [HttpPost("deletepost")] string postid  (querystring)
    const qs = new URLSearchParams({ postid });

    const response = await apiFetch(`posts/deletepost?${qs.toString()}`, {
        method: "POST",
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function addSource(postid, source) {
    // API: [HttpPost("addsource")] PostContentRequest (not FromBody, but model binder will still bind JSON)
    const req = { PostID: postid, Source: source };

    const response = await apiFetch(`posts/addsource`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function editSource(id, source) {
    const req = { ID: id, Source: source };

    const response = await apiFetch(`posts/editsource`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}

export async function deleteSource(id) {
    const req = { ID: id };

    const response = await apiFetch(`posts/deletesource`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
    });

    if (response.status === 200) return await response.json();
    return null;
}


export async function addPhotoContent(postId, index, file, userId) {
    if (!postId || !file) return null;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("index", String(index ?? 0));

    const response = await apiFetch(`/v1/posts/${postId}/content/photo`, {
        method: "POST",
        body: formData,
    });

    if (!response.ok) {
        if (response.status === 401 || response.status === 400) return response.status;
        throw new Error(`addPhotoContent failed: ${response.status}`);
    }

    return await response.json(); // postContentId
}



function getExtension(filename) {
    if (!filename) return "";
    const i = filename.lastIndexOf(".");
    return i >= 0 ? filename.substring(i) : ""; // keep leading dot like ".jpg"
}

export async function addVideoContent(postId, index, file, coverB64, userId) {
    // userId isn't needed by the API anymore if it derives from JWT,
    // but leaving the signature intact so you don't have to refactor callers.
    if (!postId || !file) return null;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("index", String(index ?? 0));
    if (coverB64) formData.append("coverB64", coverB64);

    const response = await apiFetch(`/v1/posts/${postId}/content/video`, {
        method: "POST",
        // IMPORTANT: don't set Content-Type manually for FormData
        body: formData,
    });

    if (!response.ok) {
        if (response.status === 401 || response.status === 400) return response.status;
        throw new Error(`addVideoContent failed: ${response.status}`);
    }

    return await response.json(); // postContentId
}

/* ---------- helpers ---------- */

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            // Remove: data:video/mp4;base64,
            const base64 = reader.result.split(",")[1];
            resolve(base64);
        };
        reader.onerror = reject;
    });
}

/** -----------------------------
 *  Helpers
 *  ----------------------------- */

const toBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });


//import { apiFetch } from "../../api/apiFetch";
﻿
//export async function getPosts(index, count, type, appid, filter = 0) {
//    const req = { Index: index, Count: count, Type: type, AppID: appid, Filter: filter };
//    const response = await apiFetch('/api/posts/getposts', {
//        method: 'GET',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })

//    if (response.status !== 200) {
//        if (response.status === 401 || response.status === 400)
//            return response.status;
//        return 'error';
//    }
       
//    const data = await response.json();
//    if (data)
//        return data;
//    return null;
//}

//export async function getPostsByUser(postuserid, index, count, type, appid) {
//    const req = { PostUserID: postuserid, Index: index, Count: count, Type: type, AppID: appid };
//    const response = await apiFetch('/api/posts/getpostsbyuser', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status !== 200) {
//        if (response.status === 401 || response.status === 400)
//            return response.status;
//        return 'error';
//    }
//    const data = await response.json();
//    if (data)
//        return data;
//    return null;
//}

//export async function getPostsByIdRange(postids, index, count) {
//    const req = { IDs: postids, Index: index, Count: count};
//    const response = await apiFetch('/api/posts/getpostsbyidrange', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })

//    if (response.status !== 200) {
//        if (response.status === 401)
//            return response.status;
//        return 'error';
//    }

//    const data = await response.json();
//    if (data)
//        return data;
//    return null;
//}

//export async function getNewPosts(type, id, appid, filter = 0) {
//    const req = { Type: type, PostID: id, AppID: appid, Filter: filter };
//    const response = await apiFetch('/api/posts/getnewposts', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })

//    if (response.status !== 200) {
//        if (response.status === 401)
//            return response.status;
//        return 'error';
//    }

//    const data = await response.json();
//    if (data)
//        return data;
//    return null;
//}

//export async function getPost(id) {
//    let url = '/api/posts/getpostdetails?postid=' + id;
//    const response = await apiFetch(url);

//    if (response.status !== 200) {
//        if (response.status === 401)
//            return response.status;
//        return 'error';
//    }

//    const data = await response.json();
//    if (data)
//        return data;
//    return null;
//}

//export async function getPostContent(id) {
//    const req = { ID: id };
//    const response = await apiFetch('/api/posts/getpostcontent', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const post = await response.json();
//        return post;
//    }
//    return null;
//}

//export async function newMobilePhotoPost(textcontent, photocontent, appid) {
//    const b64 = await toBase64(photocontent);
//    const photob64 = b64.split(',')[1];
//    const req = { PhotoContent: photob64, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/createmobilephotopost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newPost(userid, type, appid, category) {
//    const req = { OwnerType: 0, OwnerID: userid, Type: type, AppID: appid, TextCategory: category };
//    const response = await apiFetch('/api/posts/createpost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function repost(userid, postid, textcontent, appid) {
//    const req = { OwnerType: 0, OwnerID: userid, PostID: postid, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/repost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newBlogPost(userid, blogid, textcontent, appid) {
//    const req = { OwnerType: 0, OwnerID: userid, RefID: blogid, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/createblogpost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newEventPost(userid, eventid, textcontent, appid) {
//    const req = { OwnerType: 0, OwnerID: userid, RefID: eventid, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/createeventpost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newMarketPost(userid, itemid, textcontent, appid) {
//    const req = { OwnerType: 0, OwnerID: userid, RefID: itemid, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/createmarketpost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newVideoPost(userid, videoid, textcontent, appid) {
//    const req = { OwnerType: 0, OwnerID: userid, RefID: videoid, TextContent: textcontent, AppID: appid  };
//    const response = await apiFetch('/api/posts/createvideopost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newPhotoPost(userid, photoid, textcontent, appid) {
//    const req = { OwnerType: 0, OwnerID: userid, RefID: photoid, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/createphotopost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newSongPost(userid, songid, textcontent, appid) {
//    const req = { OwnerType: 0, OwnerID: userid, RefID: songid, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/createsongpost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function newPdfDocumentPost(userid, docid, textcontent,appid) {
//    const req = { OwnerType: 0, OwnerID: userid, RefID: docid, TextContent: textcontent, AppID: appid };
//    const response = await apiFetch('/api/posts/createpdfdocumentpost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function deletePost(id) {
//    const req = { PostID: id };
//    const response = await apiFetch('/api/posts/deletepost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    });
//    if (response.status === 200) {
//        const success = await response.json();
//        return success;
//    }
//    return null;
//}

//export async function addTextContent(id, idx, type, content) {
//    const req = { PostID: id, Index: idx, Type: type, TextContent: content };
//    const response = await apiFetch('/api/posts/addpostcontent', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function addPhotoContent(id, idx, file) {
//    var formData = new FormData();
//    formData.append('file', file);
//    formData.append('postid', id);
//    formData.append('index', idx);
//    const response = await apiFetch('/api/posts/addpostphotocontent', {
//        method: 'POST',
//        body: formData
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function addVideoContent(id, idx, file, thumb) {
//    var formData = new FormData();
//    formData.append('file', file);
//    formData.append('postid', id);
//    formData.append('index', idx);
//    formData.append('coverB64', thumb);
//    const response = await apiFetch('/api/posts/addpostvideocontent', {
//        method: 'POST',
//        body: formData
//    })
//    if (response.status === 200) {
//        const id = await response.json();
//        return id;
//    }
//    return null;
//}

//export async function savePost(id) {
//    const req = { PostID: id };
//    const response = await apiFetch('/api/posts/savepost', {

//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    });
//    if (response.status === 200) {
//        return await response.json();

//    }
//    return null;
//}

//export async function unsavePost(id) {
//    const req = { PostID: id };
//    const response = await apiFetch('/api/posts/unsavepost', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    });
//    if (response.status === 200) {
//        return await response.json();
//    }
//    return null;
//}

//const toBase64 = file => new Promise((resolve, reject) => {
//    const reader = new FileReader();
//    reader.readAsDataURL(file);
//    reader.onload = () => resolve(reader.result);
//    reader.onerror = error => reject(error);
//});

//export async function addSource(postid, source) {
//    const req = { PostID: postid, Source: source };
//    const response = await apiFetch('/api/posts/addsource', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    });
//    if (response.status === 200) {
//        return await response.json();
//    }
//    return null;
//}

//export async function editSource(postid, source) {
//    const req = { ID: postid, Source: source };
//    const response = await apiFetch('/api/posts/editsource', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    });
//    if (response.status === 200) {
//        return await response.json();
//    }
//    return null;
//}

//export async function deleteSource(postid) {
//    const req = { ID: postid };
//    const response = await apiFetch('/api/posts/deletesource', {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(req)
//    });
//    if (response.status === 200) {
//        return await response.json();
//    }
//    return null;
//}

//export async function getPostTextCategories() {
//    let url = '/api/posts/getposttextcategories';
//    const response = await apiFetch(url);

//    if (response.status !== 200) {
//        if (response.status === 401)
//            return response.status;
//        return 'error';
//    }

//    const data = await response.json();
//    if (data)
//        return data;
//    return null;
//}
